import styled from "styled-components";
import { Link, useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AiOutlinePlus } from "react-icons/ai";
import { selectUser } from "../features/userSlice";
import { subscribeCommunities, toggleMembership } from "../utils/communities";

const suggestedFellows = [
    { id: "sf1", name: "Amara Diallo", handle: "@amara_d", university: "UoN", avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=60&h=60&q=80", isOnline: true },
    { id: "sf2", name: "Kevin Odhiambo", handle: "@kevsarx", university: "KU", avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=60&h=60&q=80", isOnline: false },
    { id: "sf3", name: "Lara Njeri", handle: "@larajnj", university: "Strathmore", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=60&h=60&q=80", isOnline: true },
];

const Avatar = ({ community }) => {
    if (community.avatar) {
        return <img src={community.avatar} alt={community.name} className="c-avatar" />;
    }
    return (
        <span className="c-avatar initial">
            {(community.name || "C").charAt(0).toUpperCase()}
        </span>
    );
};

export const CommunitiesSidebar = () => {
    const [followed, setFollowed] = useState([]);
    const user = useSelector(selectUser);
    const history = useHistory();
    const [communities, setCommunities] = useState([]);

    useEffect(() => {
        const unsub = subscribeCommunities(setCommunities);
        return unsub;
    }, []);

    const toggleFollow = (id) => {
        setFollowed((prev) =>
            prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
        );
    };

    const handleJoin = async (community, e) => {
        e.preventDefault();
        e.stopPropagation();
        await toggleMembership(community.id, user);
    };

    return (
        <Wrapper>
            <CommunityCard>
                <div className="heading">
                    <p>Communities</p>
                    <Link to="/communities" className="explore-link">Explore</Link>
                </div>

                <div
                    className="create-row"
                    onClick={() => history.push("/home?createcommunity=1")}
                >
                    <AiOutlinePlus className="plus-icon" />
                    <span>Create Community</span>
                </div>

                {communities.slice(0, 14).map((c) => {
                    const isMember = user && (c.members || []).includes(user.uid);
                    return (
                        <Link key={c.id} to={`/c/${c.slug}`} className="comm-row">
                            <Avatar community={c} />
                            <span className="c-name">{c.name}</span>
                            <button
                                className={`join-btn ${isMember ? "joined" : ""}`}
                                onClick={(e) => handleJoin(c, e)}
                            >
                                {isMember ? "Joined" : "Join"}
                            </button>
                        </Link>
                    );
                })}

                {communities.length === 0 && (
                    <div className="empty">
                        No communities yet. Be the first to create one.
                    </div>
                )}
            </CommunityCard>

            {/* Suggested Fellows Card */}
            <FellowsSuggestCard>
                <div className="card-header">
                    <div className="header-left">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#1DA1F2">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                        <span className="header-title">Suggested Fellows</span>
                    </div>
                    <Link to="/fellows" className="see-all">See all</Link>
                </div>
                <p className="tagline">Join over 50,000 Fellows.</p>

                {suggestedFellows.map((fellow) => (
                    <div key={fellow.id} className="fellow-row">
                        <div className="fellow-left">
                            <div className="avatar-wrap">
                                <img src={fellow.avatar} alt={fellow.name} />
                                {fellow.isOnline && <span className="online-dot" />}
                            </div>
                            <div className="fellow-info">
                                <span className="fellow-name">{fellow.name}</span>
                                <span className="fellow-handle">{fellow.handle}</span>
                                <span className="fellow-uni">{fellow.university}</span>
                            </div>
                        </div>
                        <button
                            className={`follow-btn ${followed.includes(fellow.id) ? "following" : ""}`}
                            onClick={() => toggleFollow(fellow.id)}
                        >
                            {followed.includes(fellow.id) ? "✓ Following" : "+ Follow"}
                        </button>
                    </div>
                ))}

                <Link to="/fellows">
                    <button className="discover-btn">Discover More Fellows</button>
                </Link>
            </FellowsSuggestCard>
        </Wrapper>
    )
}

const Wrapper = styled.div`
    width: 18rem;
    flex-shrink: 0;
    position: sticky;
    top: 70px;
    height: fit-content;
    max-height: calc(100vh - 90px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background-color: rgba(201, 160, 99, 0.3); border-radius: 2px; }
`;

const CommunityCard = styled.div`
    width: 100%;
    border: 1px solid rgba(201, 160, 99, 0.25);
    border-radius: 8px;
    font-size: 18px;
    box-shadow: 0 4px 20px rgba(10, 20, 40, 0.06);
    background-color: #ffffff !important;
    overflow: hidden;

    .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.6rem 1rem;
        border-bottom: 1px solid rgb(230,231,232);
        font-size: 21px;
        font-weight: 700;
        color: #0a1428;

        p { margin: 0; }

        .explore-link {
            font-size: 18px;
            font-weight: 700;
            color: #1DA1F2;
            text-decoration: none;
            &:hover { text-decoration: underline; }
        }
    }

    .create-row {
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 0.65rem 1rem;
        color: #1DA1F2;
        font-size: 19px;
        font-weight: 700;
        cursor: pointer;
        border-bottom: 1px solid rgb(230,231,232);

        .plus-icon { font-size: 18px; }

        &:hover { background-color: #fafafa; }
    }

    .comm-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0.6rem 0.9rem;
        text-decoration: none;
        color: #14171a;
        border-bottom: 1px solid rgba(230,231,232,0.6);

        &:hover { background-color: #fafafa; }

        .c-avatar {
            width: 38px; height: 38px;
            border-radius: 50%;
            object-fit: cover;
            flex-shrink: 0;
            border: 1.5px solid rgba(201, 160, 99, 0.4);

            &.initial {
                background: linear-gradient(135deg, #c9a063, #e8c47d);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: 19px;
            }
        }

        .c-name {
            flex: 1;
            min-width: 0;
            font-size: 20px;
            font-weight: 700;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .join-btn {
            flex-shrink: 0;
            font-size: 17px;
            font-weight: 700;
            padding: 4px 12px;
            border-radius: 16px;
            border: 1.5px solid #1DA1F2;
            color: #1DA1F2;
            background: transparent;
            cursor: pointer;

            &:hover { background: #e8f5fe; }

            &.joined {
                background: #1DA1F2;
                color: white;
                border-color: #1DA1F2;
            }
        }
    }

    .empty {
        padding: 0.9rem 1rem;
        color: #999;
        font-style: italic;
        font-size: 18px;
    }
`;

const FellowsSuggestCard = styled.div`
    width: 100%;
    background-color: #ffffff;
    border: 1px solid rgba(201, 160, 99, 0.25);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(10, 20, 40, 0.06);
    padding: 0.75rem 0.7rem;
    font-size: 18px;

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;

        .header-left { display: flex; align-items: center; gap: 6px; }

        .header-title { font-weight: 700; color: #0a1428; font-size: 21px; }
        .see-all { font-size: 18px; font-weight: 700; color: #1DA1F2; cursor: pointer; &:hover { text-decoration: underline; } }
    }

    .tagline { font-size: 17px; color: #999; margin-bottom: 10px; font-style: italic; }

    .fellow-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 7px 0;
        border-bottom: 1px solid rgba(201, 160, 99, 0.1);
        &:last-of-type { border-bottom: none; }

        .fellow-left { display: flex; align-items: center; gap: 9px; }

        .avatar-wrap {
            position: relative; flex-shrink: 0;
            img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 1.5px solid rgba(201,160,99,0.4); }
            .online-dot { position: absolute; bottom: 1px; right: 1px; width: 9px; height: 9px; border-radius: 50%; background-color: #22c55e; border: 1.5px solid white; }
        }

        .fellow-info {
            display: flex; flex-direction: column; gap: 1px;
            .fellow-name { font-size: 20px; font-weight: 700; color: #0a1428; line-height: 1.2; }
            .fellow-handle { font-size: 17px; color: #1DA1F2; font-weight: 600; }
            .fellow-uni { font-size: 16px; color: #888; }
        }

        .follow-btn {
            font-size: 17px; font-weight: 700; padding: 5px 12px; border-radius: 20px;
            border: 1.5px solid #1DA1F2; color: #1DA1F2; background: transparent; cursor: pointer;
            transition: all 0.2s; white-space: nowrap;
            &:hover { background-color: #1DA1F2; color: white; }
            &.following { background-color: #1DA1F2; color: white; border-color: #1DA1F2; }
        }
    }

    .discover-btn {
        margin-top: 10px; width: 100%; padding: 8px 0;
        background: linear-gradient(135deg, #c9a063, #e8c47d);
        color: white; border: none; border-radius: 20px; font-size: 19px; font-weight: 700; cursor: pointer;
        transition: opacity 0.2s;
        &:hover { opacity: 0.85; }
    }
`;